/* eslint-disable @typescript-eslint/no-explicit-any */

import { Observable } from 'rxjs';

import { Product } from './core.models';
import { Money, Pagination } from './core.models';
import { CustomerSupplier } from './customer-supplier.model';
import { FreeRequestParams } from './shared.models';
import { UnitOfCost } from './tax-rules.models';
import { SalesPoint } from './sales-point.models';
import { Establishment } from './establishment.model';
import { CancellationStatus } from './cancellation-request.model';

export enum InvoiceType {
  salesInvoice = 'sales_invoice',
  creditNote = 'credit_note',
  proformaInvoice = 'proforma_invoice',
  bapa = 'bapa'
}

export enum BillingType {
  bToB = 'BtoB',
  bToC = 'BtoC',
  bToF = 'BtoF',
  bToG = 'BtoG'
}

export enum InvoicePaymentMethod {
  bankCard = 'bank_card',
  check = 'check',
  cash = 'cash',
  mobileMoney = 'mobile_money',
  transfer = 'transfer',
  eventually = 'eventually'
}

export enum InvoiceStatus {
  draft = 'draft',
  processing = 'processing',
  validated = 'validated',
  certified = 'certified',
  received = 'received',
  cancelled = 'cancelled'
}

export enum CustomInvoiceStatus {
  processing = 'processing',
  certified = 'certified',
  proforma = 'proforma_invoice'
}

export enum InvoiceLifecycleState {
  active = 'active',
  pending = 'pending',
  cancelled = 'cancelled'
}

export const InvoiceLifecycleStateLabels: Record<
  InvoiceLifecycleState,
  string
> = {
  [InvoiceLifecycleState.active]: 'Actif',
  [InvoiceLifecycleState.pending]: "Demande d'annulation",
  [InvoiceLifecycleState.cancelled]: 'Annulé'
};

/* export type Tax = {
  code: string;
  label: string;
  rate: number;
  amount: Money;
}; */

export type Status = {
  value: InvoiceStatus | string;
  reason?: string;
};

export type ComputationProduct = {
  name: string;
  code: string;
  price: Money;
  category: string;
};

export type ComputationTaxRule = {
  code: string;
  label: string;
  unitOfCost: string;
  cost: number | Money;
  totalAmount?: Money;
  kind?: string;
};

export type InvoiceTaxExemption = {
  id: string;
  kind?: string;
  totalAmount?: Money;
  reference?: string;
  description?: string;
  attachmentId?: string;
};

export type ComputationLineItem = {
  article: ComputationProduct;
  quantity: number;
  unitMeasure: string;
  discountUnitOfCost?: UnitOfCost;
  discountCost?: number | Money;
  taxesApplied?: ComputationTaxRule[];
  disbursed?: boolean;
  proofOfDisbursement?: string;
};

export type LineItem = {
  id?: string;
  article: Product;
  quantity: number;
  unitMeasure: string;
  discount?: Money;
  discountUnitOfCost?: UnitOfCost;
  discountCost?: number;
  taxesApplied?: ComputationTaxRule[];
  amountBeforeTax?: Money;
  totalAmount?: Money;
  disbursed?: boolean;
  proofOfDisbursement?: string;
  metadata?: any[];
  createdAt?: string;
  updatedAt?: string;
  convertedAmountBeforeTax?: Money;
  convertedTotalAmount?: Money;
  convertedDiscount?: Money;
  lineParentId?: string;
  discountShare?: Money;
};

export type CreateInvoiceLineItem = {
  articleId: string;
  quantity: number;
  unitMeasure: string;
  discountUnitOfCost?: 'amount' | 'percent';
  discount?: number;
};

export type Invoice = {
  id: string;
  organizationId: string;
  salesPointId: string;
  establishmentId: string;
  establishment: string;
  accountId: string;
  type: InvoiceType;
  issuedAt: string;
  dueDate?: string;
  certificatedAt?: string | null;
  billingType: BillingType;
  reference?: string;
  paymentMethod?: InvoicePaymentMethod;
  paymentMethods: InvoicePaymentMethod[];
  status?: Status;
  stampIncluded?: boolean;
  creditNoteReason?: string;
  amountBeforeTax?: Money;
  totalAmount?: Money;
  taxesApplied?: ComputationTaxRule[];
  taxExemptions?: InvoiceTaxExemption[];
  discountUnitOfCost?: UnitOfCost;
  discountCost?: number | Money;
  discount?: Money;
  customer: CustomerSupplier;
  supplier: CustomerSupplier;
  lineItems?: LineItem[];
  createdAt: string;
  updatedAt: string;
  //to be deleted
  typeDocument?: InvoiceType;
  deletedAt?: string;
  taxAmount?: Money;
  parent?: Invoice | null;
  footerPage?: string;
  note?: string;
  conversionRate: number;
  metadata?: Migration | any[];
  relatedTaxReceiptNumber?: string;
  // XOF amounts
  totalTtcAmount?: Money;
  totalAmountWithTVA?: Money;
  totalTva?: Money;
  totalTaxHtAmount?: Money;
  totalTaxTtcAmount?: Money;
  totalDisbursedAmount?: Money;
  stampIncludedAmount?: Money;
  totalOtherTax?: Money;
  totalTax?: Money;
  netAmount?: Money;
  // Converted amounts (foreign currency)
  convertedAmountBeforeTax?: Money;
  convertedTotalTtcAmount?: Money;
  convertedTotalAmountWithTVA?: Money;
  convertedTotalAmount?: Money;
  convertedDiscount?: Money;
  convertedTotalTax?: Money;
  convertedTotalTva?: Money;
  convertedTotalOtherTax?: Money;
  convertedTotalTaxHtAmount?: Money;
  convertedTotalTaxTtcAmount?: Money;
  convertedTotalDisbursedAmount?: Money;
  convertedStampIncludedAmount?: Money;
  convertedNetAmount?: Money;
  clientSideMetas?: {
    salePoint$?: Observable<SalesPoint>;
    establishment$?: Observable<Establishment>;
  };
  lifecycleState: InvoiceLifecycleState;
  hasCancellationRequest: boolean;
  lastCancellationStatus: CancellationStatus;
};

export type Migration = {
  migrated_from?: string;
  locked: boolean;
  currency: string;
  totalReturnedAmount: number;
  lineItemsReturned: LineItemsReturned[];
  lastCreditNoteAt: string;
  lastCreditNoteId: string;
};

export type LineItemsReturned = {
  id: string;
  quantity: number;
  remainingAmount: number;
  returnedQuantity: number;
};

export type ListInvoiceRequestParams = {
  organizationId?: string;
  establishment?: string;
  type?: InvoiceType;
  billingType?: BillingType;
  status?: InvoiceStatus;
  paymentMethods?: InvoicePaymentMethod[] | string;
  customerId?: string;
  supplierId?: string;
  reference?: string;
  issuedAt?: string;
  certificatedOnly?: boolean;
} & FreeRequestParams;

export type ListInvoiceV2RequestParams = ListInvoiceRequestParams & {
  cursor?: string | null;
};

export type CreateInvoiceParams = {
  parentId?: string;
  stampIncluded?: boolean;
  creditNoteReason?: string;
  establishmentId?: string;
  salesPointId?: string;
  type: InvoiceType;
  issuedAt?: string;
  dueDate?: string;
  billingType: BillingType;
  paymentMethods: InvoicePaymentMethod[];
  amountBeforeTax?: Money;
  totalAmount?: Money;
  taxesApplied?: ComputationTaxRule[];
  discount?: Money;
  status?: InvoiceStatus;
  customer: CreateInvoiceParamsCustomer;
  supplier: {
    id: string;
    name: string;
    phoneNumber: string;
    billingType: BillingType;
    nif: string;
    country: string;
    city: string;
    district: string;
    location: string;
    taxSystem: string;
    organizationId?: string;
    activity: string;
    email?: string;
    billingCurrency?: string;
    neighborhood?: string;
    zipcode?: string;
    taxCenter?: string;
    idu?: string;
  };
  footerPage?: string;
  note?: string;
  externalReference?: string;
  conversionRate?: number;
  lineItems?: LineItem[];
  relatedTaxReceiptNumber?: string;
};

export type CreateInvoiceParamsCustomer = {
  id: string;
  name: string;
  phoneNumber: string;
  billingType: BillingType;
  billingCurrency: string;
  country: string;
  city: string;
  location: string;
  email?: string;
  nif?: string;
  taxSystem?: string;
  activity?: string;
  district?: string;
  neighborhood?: string;
  zipcode?: string;
  organizationId?: string;
  taxCenter?: string;
};

export type UpdateInvoiceParams = {
  paymentMethods?: InvoicePaymentMethod[];
  footerPage?: string;
  lineItems?: CreateInvoiceLineItem[];
  creditNoteReason?: string;
};

export type InvoiceInitializationParams = {
  type: InvoiceType;
  customer: CustomerSupplier;
  issuedAt: string;
  dueDate: string;
};

export type InvoiceListResponse = Pagination<Invoice>;
